import {
    BusinessEntity,
    emptyAddress,
    isBusinessEntityType,
    normalizeAddress,
} from './BusinessEntity';

export interface UserAccountData {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    publicationConsent: boolean;
    receivePayments: boolean;
    businessEntity: BusinessEntity;
    bankData?: {
        bankName: string;
        IBAN: string;
        chargeClientWithFee: boolean;
    };
}

function normalizeBusinessEntity(raw: any): BusinessEntity {
    const type = raw?.businessEntity?.type;
    const data = raw?.businessEntity?.data;

    if (isBusinessEntityType(type) && data) {
        if (type === 'PERSON') {
            return {
                type,
                data: {
                    address: normalizeAddress(data?.address),
                },
            };
        }

        if (type === 'SOLE_PROPRIETORSHIP') {
            return {
                type,
                data: {
                    name: String(data?.name || ''),
                    taxNumber: String(data?.taxNumber || ''),
                    regon: String(data?.regon || ''),
                    address: normalizeAddress(data?.address),
                    deliveryAddress: normalizeAddress(data?.deliveryAddress),
                },
            };
        }

        return {
            type,
            data: {
                name: String(data?.name || ''),
                taxNumber: String(data?.taxNumber || ''),
                regon: String(data?.regon || ''),
                krs: String(data?.krs || ''),
                districtCourt: String(data?.districtCourt || ''),
                commercialDivision: String(data?.commercialDivision || ''),
                address: normalizeAddress(data?.address),
                deliveryAddress: normalizeAddress(data?.deliveryAddress),
            },
        };
    }

    // Backward compatibility with legacy payload shape.
    const company = raw?.companyData;
    if (company?.name) {
        const address = normalizeAddress(company?.address);
        const deliveryAddress = normalizeAddress(company?.deliveryAddress || company?.address);
        if (company?.krs) {
            return {
                type: 'COMPANY',
                data: {
                    name: String(company?.name || ''),
                    taxNumber: String(company?.taxNumber || ''),
                    regon: String(company?.regon || ''),
                    krs: String(company?.krs || ''),
                    districtCourt: String(company?.districtCourt || ''),
                    commercialDivision: String(company?.commercialDivision || ''),
                    address,
                    deliveryAddress,
                },
            };
        }

        return {
            type: 'SOLE_PROPRIETORSHIP',
            data: {
                name: String(company?.name || ''),
                taxNumber: String(company?.taxNumber || ''),
                regon: String(company?.regon || ''),
                address,
                deliveryAddress,
            },
        };
    }

    return {
        type: 'PERSON',
        data: {
            address: normalizeAddress(raw?.personalAddress || emptyAddress()),
        },
    };
}

export function normalizeUserAccountData(raw: any): UserAccountData {
    return {
        firstName: String(raw?.firstName || ''),
        lastName: String(raw?.lastName || ''),
        phoneNumber: String(raw?.phoneNumber || ''),
        email: String(raw?.email || ''),
        publicationConsent: !!raw?.publicationConsent,
        receivePayments: !!raw?.receivePayments,
        businessEntity: normalizeBusinessEntity(raw),
        bankData: raw?.bankData ? {
            bankName: String(raw.bankData.bankName || ''),
            IBAN: String(raw.bankData.IBAN || ''),
            chargeClientWithFee: !!raw.bankData.chargeClientWithFee,
        } : undefined,
    };
}
